import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../supabaseClient';
import { useNavigate, Link } from 'react-router-dom';
import { useResponsive } from '../hooks/useIsMobile';
import { Mail, Lock, Eye, EyeOff, ArrowRight } from '../components/Icons';
import logo from '../assets/logo.png';

function Login() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const isID = i18n.language === 'id';

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      setError(isID
        ? 'Email atau password salah. Silakan coba lagi.'
        : 'Invalid email or password. Please try again.');
    } else {
      navigate('/');
    }
    setLoading(false);
  };

  const handleGoogleLogin = async () => {
    setGoogleLoading(true);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/`,
      },
    });
    if (error) {
      setError(isID
        ? 'Login dengan Google gagal. Coba lagi.'
        : 'Google login failed. Please try again.');
      setGoogleLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(forgotEmail, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) {
      setError(isID
        ? 'Gagal mengirim email reset. Pastikan email terdaftar.'
        : 'Failed to send reset email. Make sure the email is registered.');
    } else {
      setForgotSent(true);
    }
    setForgotLoading(false);
  };

  return (
    <div style={styles.page}>
      <div style={{ ...styles.card, maxWidth: isMobile ? '100%' : '440px' }}>

        {/* Logo */}
        <div style={styles.logoRow} onClick={() => navigate('/landing')}>
          <img src={logo} alt="NOWGUT" style={styles.logoImg} />
          <div>
            <div style={styles.logoName}>NOWGUT</div>
            <div style={styles.logoTagline}>
              {isID ? 'Pemantau Risiko Saluran Cerna Anak' : 'GI Risk Monitor for Children with ASD'}
            </div>
          </div>
        </div>

        {/* ===== FORGOT PASSWORD MODE ===== */}
        {showForgot ? (
          <div>
            <div style={styles.welcomeBox}>
              <h1 style={styles.title}>
                {isID ? 'Reset Password' : 'Reset Password'}
              </h1>
              <p style={styles.subtitle}>
                {isID
                  ? 'Masukkan email Anda dan kami akan mengirimkan link untuk reset password.'
                  : 'Enter your email and we\'ll send you a reset link.'}
              </p>
            </div>

            {forgotSent ? (
              <div style={styles.successBox}>
                <div style={styles.successIcon}>✅</div>
                <div style={styles.successTitle}>
                  {isID ? 'Email terkirim!' : 'Email sent!'}
                </div>
                <p style={styles.successDesc}>
                  {isID
                    ? `Link reset password sudah dikirim ke ${forgotEmail}. Cek inbox atau folder spam Anda.`
                    : `A reset link has been sent to ${forgotEmail}. Check your inbox or spam folder.`}
                </p>
                <button style={styles.btnSecondary}
                  onClick={() => { setShowForgot(false); setForgotSent(false); }}>
                  {isID ? '← Kembali ke Login' : '← Back to Login'}
                </button>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword}>
                {error && <div style={styles.errorBox}>⚠️ {error}</div>}
                <div style={styles.field}>
                  <label style={styles.label}>{isID ? 'Email' : 'Email'}</label>
                  <div style={styles.inputWrapper}>
                    <Mail size={16} style={styles.inputIcon} color="var(--body-mid)" />
                    <input
                      style={styles.inputWithIcon}
                      type="email"
                      value={forgotEmail}
                      onChange={e => setForgotEmail(e.target.value)}
                      placeholder="email@contoh.com"
                      required />
                  </div>
                </div>
                <button
                  type="submit"
                  className="btn-primary-animated"
                  style={styles.btnPrimary}
                  disabled={forgotLoading}>
                  {forgotLoading
                    ? (isID ? 'Mengirim...' : 'Sending...')
                    : (isID ? 'Kirim Link Reset' : 'Send Reset Link')}
                  {!forgotLoading && <ArrowRight size={16} strokeWidth={2} />}
                </button>
                <button
                  type="button"
                  style={styles.btnText}
                  onClick={() => { setShowForgot(false); setError(''); }}>
                  {isID ? '← Kembali ke Login' : '← Back to Login'}
                </button>
              </form>
            )}
          </div>

        ) : (
          /* ===== LOGIN MODE ===== */
          <div>
            <div style={styles.welcomeBox}>
              <h1 style={styles.title}>
                {isID ? 'Selamat datang kembali' : 'Welcome back'}
              </h1>
              <p style={styles.subtitle}>
                {isID
                  ? 'Masuk untuk melanjutkan memantau kesehatan pencernaan anak Anda.'
                  : 'Sign in to continue monitoring your child\'s digestive health.'}
              </p>
            </div>

            {error && <div style={styles.errorBox}>⚠️ {error}</div>}

            {/* Google Login Button */}
            <button
              className="btn-secondary-animated"
              style={styles.googleBtn}
              onClick={handleGoogleLogin}
              disabled={googleLoading}>
              {googleLoading ? (
                <span>{isID ? 'Menghubungkan...' : 'Connecting...'}</span>
              ) : (
                <>
                  <GoogleIcon />
                  {isID ? 'Masuk dengan Google' : 'Continue with Google'}
                </>
              )}
            </button>

            {/* Divider */}
            <div style={styles.divider}>
              <div style={styles.dividerLine} />
              <span style={styles.dividerText}>
                {isID ? 'atau masuk dengan email' : 'or sign in with email'}
              </span>
              <div style={styles.dividerLine} />
            </div>

            {/* Email Form */}
            <form onSubmit={handleLogin}>
              <div style={styles.field}>
                <label style={styles.label}>
                  {isID ? 'Alamat Email' : 'Email Address'}
                </label>
                <div style={styles.inputWrapper}>
                  <Mail size={16} style={styles.inputIcon} color="var(--body-mid)" />
                  <input
                    style={styles.inputWithIcon}
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="email@contoh.com"
                    required />
                </div>
              </div>

              <div style={styles.field}>
                <div style={styles.labelRow}>
                  <label style={styles.label}>
                    {isID ? 'Kata Sandi' : 'Password'}
                  </label>
                  <button
                    type="button"
                    style={styles.forgotLink}
                    onClick={() => { setShowForgot(true); setError(''); }}>
                    {isID ? 'Lupa password?' : 'Forgot password?'}
                  </button>
                </div>
                <div style={styles.inputWrapper}>
                  <Lock size={16} style={styles.inputIcon} color="var(--body-mid)" />
                  <input
                    style={styles.inputWithIcon}
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required />
                  <button
                    type="button"
                    style={styles.eyeBtn}
                    onClick={() => setShowPassword(!showPassword)}>
                    {showPassword
                      ? <EyeOff size={16} color="var(--body-mid)" />
                      : <Eye size={16} color="var(--body-mid)" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                className="btn-primary-animated"
                style={styles.btnPrimary}
                disabled={loading}>
                {loading
                  ? (isID ? 'Memuat...' : 'Loading...')
                  : (isID ? 'Masuk' : 'Sign In')}
                {!loading && <ArrowRight size={16} strokeWidth={2} />}
              </button>
            </form>

            {/* Register link */}
            <p style={styles.switchText}>
              {isID ? 'Belum punya akun?' : 'Don\'t have an account?'}{' '}
              <Link to="/register" style={styles.switchLink}>
                {isID ? 'Daftar gratis' : 'Sign up free'}
              </Link>
            </p>

            <p style={styles.backLink}>
              <Link to="/landing" style={styles.backLinkText}>
                {isID ? '← Kembali ke beranda' : '← Back to home'}
              </Link>
            </p>
          </div>
        )}

        {/* Language toggle */}
        <div style={styles.langRow}>
          <button
            style={styles.langBtn}
            onClick={() => i18n.changeLanguage(isID ? 'en' : 'id')}>
            {isID ? '🇬🇧 Switch to English' : '🇮🇩 Ganti ke Bahasa Indonesia'}
          </button>
        </div>

      </div>
    </div>
  );
}

// Google Icon SVG
function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
      <path d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
      <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
      <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
    </svg>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    backgroundColor: 'var(--canvas-soft)',
    backgroundImage: ` linear-gradient( rgba(255,255,255,0.82), rgba(255,255,255,0.82) ), url('https://images.unsplash.com/photo-1489710437720-ebb67ec84dd2?q=80&w=1470&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D') `, 
    backgroundSize: 'cover', 
    backgroundPosition: 'center', 
    backgroundRepeat: 'no-repeat',
    padding: '24px 16px',
  },
  card: {
    backgroundColor: 'var(--canvas)',
    padding: '40px',
    borderRadius: 'var(--radius-md)',
    width: '100%',
    border: '1px solid var(--teal-light)',
    boxShadow: 'var(--shadow-lg)',
  },
  logoRow: {
    display: 'flex', alignItems: 'center', gap: '10px',
    marginBottom: '28px', cursor: 'pointer',
  },
  logoImg: { height: '36px', width: 'auto', objectFit: 'contain' },
  logoName: { fontSize: '15px', fontWeight: '800', color: 'var(--teal-primary)', letterSpacing: '0.05em' },
  logoTagline: { fontSize: '10px', color: 'var(--body-mid)' },
  welcomeBox: { marginBottom: '24px' },
  title: { fontSize: '24px', fontWeight: '700', color: 'var(--ink)', marginBottom: '8px', letterSpacing: '-0.01em' },
  subtitle: { fontSize: '14px', color: 'var(--body)', lineHeight: '1.6' },
  errorBox: {
    backgroundColor: '#FEF2F2', color: '#DC2626',
    padding: '12px 14px', borderRadius: 'var(--radius-sm)',
    marginBottom: '16px', fontSize: '13px',
    border: '1px solid #FECACA',
  },
  successBox: {
    textAlign: 'center', padding: '20px',
    backgroundColor: 'var(--canvas-soft)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--teal-light)',
  },
  successIcon: { fontSize: '32px', marginBottom: '12px' },
  successTitle: { fontSize: '18px', fontWeight: '700', color: 'var(--ink)', marginBottom: '8px' },
  successDesc: { fontSize: '14px', color: 'var(--body)', lineHeight: '1.6', marginBottom: '20px' },
  googleBtn: {
    width: '100%',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
    backgroundColor: 'var(--canvas)',
    color: 'var(--ink)',
    border: '1.5px solid var(--teal-light)',
    padding: '13px 16px',
    borderRadius: 'var(--radius-sm)',
    fontSize: '15px', fontWeight: '600',
    cursor: 'pointer',
    marginBottom: '20px',
    boxShadow: 'var(--shadow-sm)',
  },
  divider: {
    display: 'flex', alignItems: 'center', gap: '12px',
    marginBottom: '20px',
  },
  dividerLine: { flex: 1, height: '1px', backgroundColor: 'var(--teal-light)' },
  dividerText: { fontSize: '12px', color: 'var(--body-mid)', whiteSpace: 'nowrap' },
  field: { marginBottom: '16px' },
  labelRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' },
  label: { fontSize: '13px', fontWeight: '600', color: 'var(--ink)' },
  forgotLink: {
    background: 'none', border: 'none',
    color: 'var(--teal-primary)', fontSize: '13px',
    fontWeight: '500', cursor: 'pointer',
    textDecoration: 'underline',
  },
  inputWrapper: {
    position: 'relative', display: 'flex', alignItems: 'center',
  },
  inputIcon: {
    position: 'absolute', left: '12px',
    pointerEvents: 'none', flexShrink: 0,
  },
  inputWithIcon: {
    width: '100%', padding: '12px 42px',
    borderRadius: 'var(--radius-sm)',
    border: '1.5px solid var(--teal-light)',
    fontSize: '15px', boxSizing: 'border-box',
    backgroundColor: 'var(--canvas)',
    color: 'var(--ink)',
    fontFamily: 'inherit',
  },
  eyeBtn: {
    position: 'absolute', right: '12px',
    background: 'none', border: 'none',
    cursor: 'pointer', padding: '4px',
    display: 'flex', alignItems: 'center',
  },
  btnPrimary: {
    width: '100%', padding: '14px',
    backgroundColor: 'var(--primary)',
    color: 'var(--on-primary)',
    border: 'none', borderRadius: 'var(--radius-md)',
    fontSize: '16px', fontWeight: '700',
    cursor: 'pointer', marginTop: '4px',
    display: 'flex', alignItems: 'center',
    justifyContent: 'center', gap: '8px',
    boxShadow: '0 4px 12px rgba(226,120,48,0.3)',
  },
  btnSecondary: {
    width: '100%', padding: '12px',
    backgroundColor: 'transparent',
    color: 'var(--teal-primary)',
    border: '1.5px solid var(--teal-light)',
    borderRadius: 'var(--radius-md)',
    fontSize: '14px', fontWeight: '600',
    cursor: 'pointer', marginTop: '8px',
  },
  btnText: {
    width: '100%', padding: '12px',
    background: 'none', border: 'none',
    color: 'var(--body)', fontSize: '14px',
    cursor: 'pointer', marginTop: '8px',
    textDecoration: 'underline',
  },
  switchText: { textAlign: 'center', marginTop: '20px', fontSize: '14px', color: 'var(--body)' },
  switchLink: { color: 'var(--teal-primary)', fontWeight: '700', textDecoration: 'none' },
  backLink: { textAlign: 'center', marginTop: '10px' },
  backLinkText: { fontSize: '13px', color: 'var(--body-mid)', textDecoration: 'none' },
  langRow: { marginTop: '24px', textAlign: 'center', paddingTop: '20px', borderTop: '1px solid var(--teal-light)' },
  langBtn: {
    background: 'none', border: 'none',
    color: 'var(--body-mid)', fontSize: '12px',
    cursor: 'pointer', textDecoration: 'underline',
  },
};

export default Login;